"use client";

import { useState, useEffect } from "react";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Record<string, unknown>[];
  }
}

function getUtmParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    gclid: params.get("gclid") || "",
    landing_page: window.location.pathname,
  };
}

export default function LeadForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [utmData, setUtmData] = useState<Record<string, string>>({});
  const [formStarted, setFormStarted] = useState(false);

  useEffect(() => {
    setUtmData(getUtmParams());
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("loading");

    const form = e.currentTarget;
    const data = {
      name: (form.elements.namedItem("name") as HTMLInputElement).value,
      email: (form.elements.namedItem("email") as HTMLInputElement).value,
      phone: (form.elements.namedItem("phone") as HTMLInputElement).value,
      company: (form.elements.namedItem("company") as HTMLInputElement).value,
      website: (form.elements.namedItem("website") as HTMLInputElement).value, // honeypot
      ...utmData,
    };

    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        form.reset();
        // Push to dataLayer for GTM triggers
        window.dataLayer?.push({
          event: "pilot_request_submitted",
          form_name: "contact_pilot",
          form_location: "homepage_footer",
        });
        // Track conversion in GA4 — use event_callback to delay redirect
        // until the event beacon is sent, preventing data loss
        let redirected = false;
        const redirect = () => {
          if (!redirected) {
            redirected = true;
            window.location.href = "/bedankt";
          }
        };
        window.gtag?.("event", "generate_lead", {
          event_category: "form",
          event_label: "demo_request",
          value: 1,
          event_callback: redirect,
        });
        // Fallback: redirect after 1s if callback never fires (e.g. ad blocker)
        setTimeout(redirect, 1000);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="py-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/20">
          <span className="text-2xl text-green-400">✓</span>
        </div>
        <h3 className="mb-2 text-xl font-bold text-white">Bedankt!</h3>
        <p className="mb-4 text-sm text-gray-300">
          Je aanvraag is ontvangen. Dit is wat er nu gebeurt:
        </p>
        <ul className="mx-auto mb-4 max-w-xs space-y-2 text-left text-sm text-gray-300">
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-green-400">✓</span>
            <span>We nemen binnen 24 uur contact op</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-green-400">✓</span>
            <span>Demo van 20 minuten wordt ingepland</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-green-400">✓</span>
            <span>Voorstel op maat voor jouw team</span>
          </li>
        </ul>
        <p className="text-xs text-gray-500">Meestal reageren we binnen 2-4 uur op werkdagen.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="sr-only">
          Naam
        </label>
        <input
          type="text"
          id="name"
          name="name"
          placeholder="Naam"
          required
          autoComplete="name"
          onFocus={() => {
            if (!formStarted) {
              setFormStarted(true);
              if (typeof window !== "undefined" && window.gtag) {
                window.gtag("event", "form_start", {
                  form_name: "pilot_request",
                  form_location: "homepage_footer",
                });
              }
            }
          }}
          className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-500 transition-colors outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/25"
        />
      </div>
      <div>
        <label htmlFor="email" className="sr-only">
          E-mailadres
        </label>
        <input
          type="email"
          id="email"
          name="email"
          placeholder="E-mailadres"
          required
          autoComplete="email"
          className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-500 transition-colors outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/25"
        />
      </div>
      <div>
        <label htmlFor="phone" className="sr-only">
          Telefoonnummer
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          placeholder="Telefoonnummer (optioneel)"
          autoComplete="tel"
          className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-500 transition-colors outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/25"
        />
      </div>
      <div>
        <label htmlFor="company" className="sr-only">
          Bedrijfsnaam
        </label>
        <input
          type="text"
          id="company"
          name="company"
          placeholder="Bedrijfsnaam"
          required
          autoComplete="organization"
          className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-500 transition-colors outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/25"
        />
      </div>
      {/* Honeypot — hidden from real users, filled by bots */}
      <input
        type="text"
        name="website"
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition-[box-shadow,filter,transform] hover:shadow-blue-500/40 hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
      >
        {status === "loading" ? "Even geduld..." : "Vraag demo aan"}
      </button>
      {status === "error" && (
        <p className="text-xs text-red-400">
          Er ging iets mis. Probeer het opnieuw of mail naar info@elusiveinnovations.com.
        </p>
      )}
      <p className="text-xs text-gray-500">
        Geen creditcard nodig &middot; Vrijblijvend &middot; Reactie binnen 24 uur
      </p>
    </form>
  );
}
